import { cn, useIsMobile, TooltipProvider } from '@lqa-frontend/ui-system';
import * as React from 'react';
import {
	SIDEBAR_COOKIE_MAX_AGE,
	SIDEBAR_COOKIE_NAME,
	SIDEBAR_KEYBOARD_SHORTCUT,
	SIDEBAR_TOOLTIP_DELAY_MS,
	SIDEBAR_WIDTH,
	SIDEBAR_WIDTH_ICON,
	SIDEBAR_WIDTH_MOBILE,
	SidebarState,
} from './types';
import type {
	ICssVariableStyle,
	ISidebarContext,
	ISidebarProviderProps,
} from './types';

const SidebarContext = React.createContext<ISidebarContext | null>(null);

function useSidebar() {
	const context = React.useContext(SidebarContext);
	if (!context) {
		throw new Error('useSidebar must be used within a SidebarProvider.');
	}

	return context;
}

// The cookie is written on every toggle, so it has to be read back here to
// restore the pinned state.
function getPersistedIsOpen(): boolean | null {
	if (typeof document === 'undefined') {
		return null;
	}

	const entry = document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(`${SIDEBAR_COOKIE_NAME}=`));

	if (!entry) {
		return null;
	}

	return entry.slice(SIDEBAR_COOKIE_NAME.length + 1) === 'true';
}

function SidebarProvider({
	defaultOpen = true,
	isOpen: controlledIsOpen,
	onOpenChange,
	width = SIDEBAR_WIDTH,
	collapsedWidth = SIDEBAR_WIDTH_ICON,
	mobileWidth = SIDEBAR_WIDTH_MOBILE,
	className,
	style,
	children,
	...props
}: ISidebarProviderProps) {
	const isMobile = useIsMobile();
	const [isMobileOpen, setIsMobileOpen] = React.useState(false);
	const [isHoverExpanded, setIsHoverExpanded] = React.useState(false);

	// This is the internal state of the sidebar.
	// We use controlledIsOpen and onOpenChange for control from outside the component.
	const [internalIsOpen, setInternalIsOpen] = React.useState(
		() => getPersistedIsOpen() ?? defaultOpen
	);
	const isOpen = controlledIsOpen ?? internalIsOpen;
	const setIsOpen = React.useCallback(
		(value: boolean | ((value: boolean) => boolean)) => {
			const nextIsOpen = typeof value === 'function' ? value(isOpen) : value;
			if (onOpenChange) {
				onOpenChange(nextIsOpen);
			} else {
				setInternalIsOpen(nextIsOpen);
			}

			// This sets the cookie to keep the sidebar state.
			document.cookie = `${SIDEBAR_COOKIE_NAME}=${nextIsOpen}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
		},
		[onOpenChange, isOpen]
	);

	// Helper to toggle the sidebar. Clearing the hover peek lets an explicit
	// collapse take effect while the pointer is still over the sidebar.
	const toggleSidebar = React.useCallback(() => {
		if (isMobile) {
			setIsMobileOpen((prev) => !prev);
			return;
		}

		setIsHoverExpanded(false);
		setIsOpen((prev) => !prev);
	}, [isMobile, setIsOpen, setIsMobileOpen, setIsHoverExpanded]);

	// Adds a keyboard shortcut to toggle the sidebar.
	React.useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
				(event.metaKey || event.ctrlKey)
			) {
				event.preventDefault();
				toggleSidebar();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [toggleSidebar]);

	// We add a state so that we can do data-state="expanded" or "collapsed".
	// This makes it easier to style the sidebar with Tailwind classes.
	// Hovering a collapsed sidebar expands it temporarily without changing the
	// pinned state, so it collapses again once the pointer leaves.
	const state =
		isOpen || isHoverExpanded ? SidebarState.Expanded : SidebarState.Collapsed;

	const contextValue = React.useMemo<ISidebarContext>(
		() => ({
			state,
			isOpen,
			setIsOpen,
			isHoverExpanded,
			setIsHoverExpanded,
			isMobile,
			isMobileOpen,
			setIsMobileOpen,
			toggleSidebar,
			mobileWidth,
		}),
		[
			state,
			isOpen,
			setIsOpen,
			isHoverExpanded,
			setIsHoverExpanded,
			isMobile,
			isMobileOpen,
			setIsMobileOpen,
			toggleSidebar,
			mobileWidth,
		]
	);

	// The widths travel to the CSS as custom properties so every descendant can
	// size off them, but they are set through named props rather than asking
	// callers to know the variable names.
	const wrapperStyle: ICssVariableStyle = {
		...style,
		'--sidebar-width': width,
		'--sidebar-width-icon': collapsedWidth,
	};

	return (
		<SidebarContext.Provider value={contextValue}>
			<TooltipProvider delayDuration={SIDEBAR_TOOLTIP_DELAY_MS}>
				<div
					data-slot="sidebar-wrapper"
					style={wrapperStyle}
					className={cn(
						'group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full',
						className
					)}
					{...props}
				>
					{children}
				</div>
			</TooltipProvider>
		</SidebarContext.Provider>
	);
}

export { SidebarProvider, useSidebar };
